<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("menu.app_catalog_coupons") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <base-button
                        v-if="isAdmin"
                        size="md"
                        type="neutral"
                        @click="openCreate"
                    >
                        <i class="fa fa-plus"></i>
                        {{ $t("app_catalog.btn_add_coupon_template") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
                <template slot="header">
                    <div class="row">
                        <div class="col-md-6">
                            <base-input
                                v-model="keyword"
                                prepend-icon="fas fa-search"
                                :placeholder="$t('search')"
                                @keyup.enter.native="loadList"
                            ></base-input>
                        </div>
                        <div class="col-md-2">
                            <base-button type="primary" size="sm" @click="loadList">
                                <i class="fa fa-search"></i> {{ $t("search") }}
                            </base-button>
                        </div>
                    </div>
                </template>

                <div v-loading="loading">
                    <el-table
                        :data="pagedList"
                        row-key="id"
                        header-row-class-name="thead-light"
                        :empty-text="$t('app_catalog.no_data')"
                    >
                        <el-table-column label="ID" prop="id" min-width="60px"></el-table-column>
                        <el-table-column :label="$t('app_catalog.coupon_code')" prop="code" min-width="120px"></el-table-column>
                        <el-table-column :label="$t('app_catalog.col_name_th')" prop="name_th" min-width="140px"></el-table-column>
                        <el-table-column :label="$t('app_catalog.coupon_discount_type')" prop="discount_type" min-width="100px">
                            <template slot-scope="{ row }">
                                <el-tag size="small">{{ $t('app_catalog.discount_type_' + row.discount_type) }}</el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.coupon_discount_value')" prop="discount_value" min-width="90px"></el-table-column>
                        <el-table-column :label="$t('app_catalog.col_issued_quota')" min-width="90px">
                            <template slot-scope="{ row }">{{ issuedQuotaLabel(row) }}</template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.coupon_is_active')" min-width="80px">
                            <template slot-scope="{ row }">
                                <el-tag :type="row.is_active ? 'success' : 'info'" size="small">
                                    {{ row.is_active ? $t("app_catalog.enabled") : $t("app_catalog.disabled") }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('actions')" min-width="120px" align="right">
                            <template slot-scope="{ row }">
                                <base-button
                                    v-if="isAdmin"
                                    size="sm"
                                    type="warning"
                                    class="mr-1"
                                    @click="openEdit(row)"
                                >
                                    <i class="fa fa-edit"></i>
                                </base-button>
                                <base-button
                                    v-if="isAdmin"
                                    size="sm"
                                    type="danger"
                                    @click="confirmDeactivate(row)"
                                >
                                    <i class="fa fa-ban"></i>
                                </base-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <template slot="footer">
                    <div class="d-flex justify-content-end mt-3">
                        <el-pagination
                            background
                            layout="prev, pager, next"
                            :total="filteredList.length"
                            :page-size="pageSize"
                            :current-page="page"
                            @current-change="onPageChange"
                        ></el-pagination>
                    </div>
                </template>
            </card>
        </div>

        <!-- Create / Edit Dialog -->
        <el-dialog
            :title="editMode ? $t('app_catalog.edit_coupon_template') : $t('app_catalog.btn_add_coupon_template')"
            :visible.sync="dialogVisible"
            width="640px"
            @closed="resetForm"
        >
            <el-form
                ref="couponForm"
                :model="form"
                :rules="rules"
                label-width="160px"
            >
                <el-form-item :label="$t('app_catalog.coupon_code')" prop="code">
                    <el-input :value="form.code" @input="onCodeInput"></el-input>
                    <div v-if="isPublicCodeForm" class="text-muted small mt-1 public-code-hint">
                        {{ $t("app_catalog.public_code_hint") }}
                    </div>
                </el-form-item>
                <el-form-item :label="$t('app_catalog.col_name_th')" prop="name_th">
                    <el-input v-model="form.name_th"></el-input>
                </el-form-item>
                <el-form-item :label="$t('app_catalog.col_name_en')">
                    <el-input v-model="form.name_en"></el-input>
                </el-form-item>
                <el-form-item :label="$t('app_catalog.coupon_discount_type')" prop="discount_type">
                    <el-select v-model="form.discount_type" class="w-100">
                        <el-option :label="$t('app_catalog.discount_type_percent')" value="percent"></el-option>
                        <el-option :label="$t('app_catalog.discount_type_fixed')" value="fixed"></el-option>
                        <el-option :label="$t('app_catalog.discount_type_shipping_free')" value="shipping_free"></el-option>
                    </el-select>
                </el-form-item>
                <el-form-item :label="$t('app_catalog.coupon_discount_value')" prop="discount_value">
                    <el-input-number v-model="form.discount_value" :min="0" :precision="2"></el-input-number>
                </el-form-item>
                <el-form-item :label="$t('app_catalog.coupon_max_discount')">
                    <el-input-number v-model="form.max_discount" :min="0" :precision="2"></el-input-number>
                </el-form-item>
                <el-form-item :label="$t('app_catalog.coupon_min_purchase')">
                    <el-input-number v-model="form.min_purchase" :min="0" :precision="2"></el-input-number>
                </el-form-item>
                <el-form-item :label="$t('app_catalog.coupon_expiry_days')">
                    <el-input-number v-model="form.expiry_days" :min="isPublicCodeForm ? 1 : 0"></el-input-number>
                </el-form-item>
                <el-form-item :label="$t('app_catalog.coupon_valid_from')">
                    <el-date-picker
                        v-model="form.valid_from"
                        type="datetime"
                        value-format="yyyy-MM-dd HH:mm:ss"
                        style="width: 240px;"
                    ></el-date-picker>
                </el-form-item>
                <el-form-item :label="$t('app_catalog.coupon_valid_to')">
                    <el-date-picker
                        v-model="form.valid_to"
                        type="datetime"
                        value-format="yyyy-MM-dd HH:mm:ss"
                        style="width: 240px;"
                    ></el-date-picker>
                </el-form-item>
                <el-form-item :label="$t('app_catalog.coupon_eligibility')">
                    <el-select :value="form.eligibility" class="w-100" @change="onEligibilityChange">
                        <el-option :label="$t('app_catalog.eligibility_any')" value="any"></el-option>
                        <el-option :label="$t('app_catalog.eligibility_new_member')" value="new_member"></el-option>
                        <el-option :label="$t('app_catalog.eligibility_birthday')" value="birthday"></el-option>
                        <el-option :label="$t('app_catalog.eligibility_level_up')" value="level_up"></el-option>
                        <el-option :label="$t('app_catalog.eligibility_manual')" value="manual"></el-option>
                        <el-option :label="$t('app_catalog.eligibility_public_code')" value="public_code"></el-option>
                    </el-select>
                </el-form-item>
                <el-form-item :label="$t('app_catalog.coupon_points_cost')">
                    <el-input-number v-model="form.points_cost" :min="0"></el-input-number>
                </el-form-item>
                <el-form-item :label="$t('app_catalog.coupon_max_use_per_member')">
                    <el-input-number v-model="form.max_use_per_member" :min="0"></el-input-number>
                </el-form-item>
                <el-form-item :label="$t('app_catalog.coupon_total_quota')">
                    <el-input-number v-model="form.total_quota" :min="0"></el-input-number>
                </el-form-item>
                <el-form-item :label="$t('app_catalog.coupon_is_active')">
                    <el-switch v-model="form.is_active" :active-value="true" :inactive-value="false"></el-switch>
                </el-form-item>
            </el-form>
            <span slot="footer" class="dialog-footer">
                <base-button type="secondary" @click="dialogVisible = false">{{ $t("cancel") || "Cancel" }}</base-button>
                <base-button type="primary" :loading="saving" @click="submitForm">{{ $t("save") }}</base-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import { apiErrorMessage } from "@/util/api-error";
import {
    isPublicCode,
    normalizePublicCode,
    onEligibilityChange as patchOnEligibilityChange,
    validatePublicCodeForm,
    issuedQuotaLabel,
    publicCodeErrorKey
} from "@/utils/publicCouponCode";
import { mapGetters, mapActions } from "vuex";
import swal from "sweetalert2";
import {
    Table,
    TableColumn,
    Tag,
    Pagination,
    Dialog,
    Form,
    FormItem,
    Input,
    Select,
    Option,
    InputNumber,
    DatePicker,
    Switch,
} from "element-ui";

export default {
    name: "AppCatalogCouponTemplates",
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Pagination.name]: Pagination,
        [Dialog.name]: Dialog,
        [Form.name]: Form,
        [FormItem.name]: FormItem,
        [Input.name]: Input,
        [Select.name]: Select,
        [Option.name]: Option,
        [InputNumber.name]: InputNumber,
        [DatePicker.name]: DatePicker,
        [Switch.name]: Switch,
    },
    data() {
        return {
            keyword: "",
            page: 1,
            pageSize: 20,
            dialogVisible: false,
            editMode: false,
            saving: false,
            form: {
                code: "",
                name_th: "",
                name_en: "",
                discount_type: "percent",
                discount_value: 0,
                max_discount: 0,
                min_purchase: 0,
                expiry_days: 0,
                valid_from: null,
                valid_to: null,
                eligibility: "any",
                points_cost: 0,
                max_use_per_member: 1,
                total_quota: 0,
                is_active: true
            },
            rules: {
                code: [{ required: true, message: this.$t("app_catalog.field_required"), trigger: "blur" }],
                name_th: [{ required: true, message: this.$t("app_catalog.field_required"), trigger: "blur" }],
                discount_type: [{ required: true, message: this.$t("app_catalog.field_required"), trigger: "change" }],
                discount_value: [{ required: true, type: "number", message: this.$t("app_catalog.field_required"), trigger: "blur" }]
            }
        };
    },
    computed: {
        ...mapGetters({
            list: "AppCatalogCouponTemplate/list",
            loading: "AppCatalogCouponTemplate/loading",
            isAdmin: "Auth/isAdmin"
        }),
        filteredList() {
            if (!this.keyword) return this.list;
            const kw = this.keyword.toLowerCase();
            return this.list.filter(item =>
                (item.code && item.code.toLowerCase().includes(kw)) ||
                (item.name_th && item.name_th.toLowerCase().includes(kw))
            );
        },
        isPublicCodeForm() {
            return isPublicCode(this.form.eligibility);
        },
        pagedList() {
            const start = (this.page - 1) * this.pageSize;
            return this.filteredList.slice(start, start + this.pageSize);
        }
    },
    watch: {
        keyword() {
            this.page = 1;
        }
    },
    mounted() {
        this.loadList();
    },
    methods: {
        ...mapActions({
            fetchList: "AppCatalogCouponTemplate/fetchList",
            createItem: "AppCatalogCouponTemplate/create",
            updateItem: "AppCatalogCouponTemplate/update",
            removeItem: "AppCatalogCouponTemplate/remove"
        }),
        issuedQuotaLabel,
        onCodeInput(val) {
            this.form.code = this.isPublicCodeForm ? normalizePublicCode(val) : val;
        },
        onEligibilityChange(val) {
            this.form = patchOnEligibilityChange(this.form, val);
        },
        loadList() {
            this.fetchList({ page: this.page, limit: 20 });
        },
        onPageChange(p) {
            // BE returns the full list; paging is purely client-side over filteredList.
            this.page = p;
        },
        openCreate() {
            this.editMode = false;
            this.dialogVisible = true;
        },
        openEdit(row) {
            this.editMode = true;
            this.form = {
                id: row.id,
                code: row.code || "",
                name_th: row.name_th || "",
                name_en: row.name_en || "",
                discount_type: row.discount_type || "percent",
                discount_value: row.discount_value || 0,
                max_discount: row.max_discount || 0,
                min_purchase: row.min_purchase || 0,
                expiry_days: row.expiry_days || 0,
                valid_from: this.toPickerLocal(row.valid_from),
                valid_to: this.toPickerLocal(row.valid_to),
                eligibility: row.eligibility || "any",
                points_cost: row.points_cost || 0,
                max_use_per_member: row.max_use_per_member || 1,
                total_quota: row.total_quota || 0,
                is_active: row.is_active !== undefined ? row.is_active : true
            };
            this.dialogVisible = true;
        },
        resetForm() {
            this.form = {
                code: "", name_th: "", name_en: "", discount_type: "percent",
                discount_value: 0, max_discount: 0, min_purchase: 0,
                expiry_days: 0, valid_from: null, valid_to: null,
                eligibility: "any", points_cost: 0, max_use_per_member: 1,
                total_quota: 0, is_active: true
            };
            if (this.$refs.couponForm) {
                this.$refs.couponForm.resetFields();
            }
        },
        submitForm() {
            this.$refs.couponForm.validate(async (valid) => {
                if (!valid) return;
                const publicErrs = validatePublicCodeForm(this.form);
                if (publicErrs.length) {
                    this.$notify({ type: "danger", message: publicErrs.map(k => this.$t(k)).join(" · ") });
                    return;
                }
                this.saving = true;
                try {
                    const payload = { ...this.form };
                    delete payload.id;
                    if (isPublicCode(payload.eligibility)) {
                        payload.code = normalizePublicCode(payload.code);
                    }
                    // Pickers emit local wall-clock ("yyyy-MM-dd HH:mm:ss"); convert to a
                    // true RFC3339 instant so BE stores the right moment (avoids the
                    // fake-UTC literal-Z shift from serializing raw Date objects).
                    payload.valid_from = this.toIsoInstant(this.form.valid_from);
                    payload.valid_to = this.toIsoInstant(this.form.valid_to);

                    if (this.editMode) {
                        await this.updateItem({ id: this.form.id, payload });
                    } else {
                        await this.createItem(payload);
                    }

                    this.dialogVisible = false;
                    this.loadList();
                    this.$notify({ type: "success", message: this.$t("app_catalog.save_success") });
                } catch (err) {
                    const pubKey = publicCodeErrorKey(err);
                    const msg = pubKey ? this.$t(pubKey) : apiErrorMessage(err, this.$t);
                    this.$notify({ type: "danger", message: msg });
                } finally {
                    this.saving = false;
                }
            });
        },
        confirmDeactivate(row) {
            swal.fire({
                title: this.$t("app_catalog.deactivate_title"),
                text: this.$t("app_catalog.deactivate_msg"),
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                focusCancel: true,
                confirmButtonText: this.$t("app_catalog.btn_deactivate"),
                cancelButtonText: this.$t("app_catalog.btn_cancel"),
                confirmButtonColor: "#d33",
                cancelButtonColor: "#6c757d"
            }).then(async (result) => {
                if (result.value) {
                    try {
                        await this.removeItem(row.id);
                        this.loadList();
                        this.$notify({ type: "success", message: this.$t("app_catalog.deactivate_success") });
                    } catch (err) {
                        const msg = apiErrorMessage(err, this.$t);
                        this.$notify({ type: "danger", message: msg });
                    }
                }
            });
        },
        toPickerLocal(dateStr) {
            // Convert an RFC3339 instant from BE into the picker's local
            // wall-clock format ("yyyy-MM-dd HH:mm:ss").
            if (!dateStr) return null;
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return null;
            const pad = n => String(n).padStart(2, "0");
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        },
        toIsoInstant(localStr) {
            // Picker emits local wall-clock ("yyyy-MM-dd HH:mm:ss"); convert to a
            // true RFC3339 instant so BE stores the right moment.
            if (!localStr) return null;
            const d = new Date(localStr.replace(" ", "T"));
            if (isNaN(d.getTime())) return null;
            return d.toISOString();
        }
    }
};
</script>
